package org.vreo.devicehub.features.vrmOutput.presentation

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.List
import androidx.compose.material.icons.automirrored.filled.Rule
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Cloud
import androidx.compose.material.icons.filled.DirectionsCar
import androidx.compose.material.icons.filled.ErrorOutline
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Layers
import androidx.compose.material.icons.filled.Link
import androidx.compose.material.icons.filled.LinkOff
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.LockOpen
import androidx.compose.material.icons.filled.PowerSettingsNew
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Save
import androidx.compose.material.icons.outlined.Cloud
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.ListItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog
import org.vreo.devicehub.features.core.domain.device.vrmConfig.VrmOutputConfig
import org.vreo.devicehub.features.core.domain.device.vrmConfig.VrmOutputType
import org.vreo.devicehub.features.core.presentation.components.configTextField.ConfigTextField
import org.vreo.devicehub.features.core.presentation.components.largeDropdownMenu.LargeDropdownMenu
import org.vreo.devicehub.features.vrmOutput.domain.models.VrmOverlayerPosition
import org.vreo.devicehub.features.vrmOutput.presentation.components.VrmList
import org.vreo.devicehub.features.vrmOutput.presentation.components.toUiText

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.load_config_button_title
import devicehub.composeapp.generated.resources.save_button_title
import devicehub.composeapp.generated.resources.device_control_unavailable_label
import devicehub.composeapp.generated.resources.vrem_overlayer_bay_id_label
import devicehub.composeapp.generated.resources.vrm_bayid_note
import devicehub.composeapp.generated.resources.vrm_list_item_add_vrm_button_label
import devicehub.composeapp.generated.resources.vrm_list_item_label
import devicehub.composeapp.generated.resources.vrm_output_api_key_label
import devicehub.composeapp.generated.resources.vrm_output_confidence_threshold_label
import devicehub.composeapp.generated.resources.vrm_output_connection_details_label
import devicehub.composeapp.generated.resources.vrm_output_device_hiub_title
import devicehub.composeapp.generated.resources.vrm_output_enable_label
import devicehub.composeapp.generated.resources.vrm_output_endpoint_label
import devicehub.composeapp.generated.resources.vrm_output_roi_switch_label
import devicehub.composeapp.generated.resources.vrm_output_screen_title
import devicehub.composeapp.generated.resources.vrm_output_site_id_label
import devicehub.composeapp.generated.resources.vrm_output_time_label
import devicehub.composeapp.generated.resources.vrm_output_type_label
import devicehub.composeapp.generated.resources.vrm_output_validation_settings_label
import devicehub.composeapp.generated.resources.vrm_output_verify_ssl_label
import devicehub.composeapp.generated.resources.vrm_overlayer_auto_timezone_label
import devicehub.composeapp.generated.resources.vrm_overlayer_custom_label
import devicehub.composeapp.generated.resources.vrm_overlayer_date_and_time_label
import devicehub.composeapp.generated.resources.vrm_overlayer_ocr_results_label
import devicehub.composeapp.generated.resources.vrm_overlayer_title
import org.vreo.devicehub.features.core.presentation.components.sectionHeader.SectionHeader

/**
 * Vrm output screen root, displays vrm output screen.
 *
 * @param viewModel view model for vrm output screen
 */
@Composable
fun VrmOutputScreenRoot(
    viewModel: VrmOutputViewModel = koinViewModel(),
) {
    val state: VrmOutputState by viewModel.state.collectAsStateWithLifecycle()

    VrmOutputScreenView(
        state = state,
        miniConfidenceScoreLimit = viewModel.miniConfidenceScoreLimit,
        maxConfidenceScoreLimit = viewModel.maxConfidenceScoreLimit,
        miniTimeoutSecondsLimit = viewModel.miniTimeoutSeconds,
        maxTimeoutSecondsLimit = viewModel.maxTimeoutSeconds,
        availableRoi = viewModel.availableRoi,
        onAction = { action ->
            viewModel.onAction(action)
        }
    )
}

/**
 * Vrm output screen view. Presents the list of configured VRM outputs plus the editable form for
 * whichever entry is currently selected.
 *
 * @param state state to present
 * @param onAction action handler
 * @param scrollState scroll state
 * @param miniConfidenceScoreLimit lower bound shown/enforced for the confidence threshold field
 * @param maxConfidenceScoreLimit upper bound shown/enforced for the confidence threshold field
 * @param miniTimeoutSecondsLimit lower bound shown/enforced for the timeout field
 * @param maxTimeoutSecondsLimit upper bound shown/enforced for the timeout field
 * @param availableRoi Available ROI identifiers
 */
@Composable
fun VrmOutputScreenView(
    state: VrmOutputState,
    onAction: (VrmOutputActions) -> Unit,
    scrollState: ScrollState = rememberScrollState(),
    miniConfidenceScoreLimit: Float = 0.0f,
    maxConfidenceScoreLimit: Float = 1.0f,
    miniTimeoutSecondsLimit: Int = 0,
    maxTimeoutSecondsLimit: Int = 1,
    availableRoi: List<Int> = listOf()
) {
    // Handling Dialogs
    if (state.isLoading) LoadingDialog()
    state.error?.let { error ->
        ErrorDialog(
            dialogTitle = stringResource(Res.string.error_general_title),
            dialogText = error.asString(),
            onDismissRequest = { onAction(VrmOutputActions.OnClearError) },
            onConfirmation = { onAction(VrmOutputActions.OnClearError) }
        )
    }

    Column(
        modifier = Modifier
            .fillMaxHeight()
            .widthIn(max = 1400.dp)
            .verticalScroll(scrollState)
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(20.dp)
    ) {
        // Screen title with icon
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Icon(
                imageVector = Icons.Default.DirectionsCar,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(28.dp)
            )
            Text(
                text = stringResource(Res.string.vrm_output_screen_title),
                style = MaterialTheme.typography.headlineSmall,
            )
        }

        // Device unavailable warning banner
        if (!state.isDeviceActive) {
            Surface(
                color = MaterialTheme.colorScheme.errorContainer,
                shape = MaterialTheme.shapes.small,
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.ErrorOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onErrorContainer
                    )
                    Text(
                        text = stringResource(Res.string.device_control_unavailable_label),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onErrorContainer
                    )
                }
            }
        }

        // Device Hub reporting control
        OutlinedCard(
            shape = MaterialTheme.shapes.medium,
            modifier = Modifier.fillMaxWidth()
        ) {
            ListItem(
                leadingContent = {
                    Icon(
                        imageVector = if (state.deviceHubReportingEnabled) Icons.Filled.Cloud else Icons.Outlined.Cloud,
                        contentDescription = null,
                        tint = if (state.vrmEnabled) MaterialTheme.colorScheme.primary
                        else MaterialTheme.colorScheme.onSurfaceVariant
                    )
                },
                headlineContent = { Text(stringResource(Res.string.vrm_output_device_hiub_title)) },
                trailingContent = {
                    Switch(
                        checked = state.deviceHubReportingEnabled,
                        onCheckedChange = { onAction(VrmOutputActions.OnUpdateReportingToDeviceHub(it)) }
                    )
                }
            )
        }

        // VRM list management card
        OutlinedCard(
            shape = MaterialTheme.shapes.medium,
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(
                verticalArrangement = Arrangement.spacedBy(16.dp),
                modifier = Modifier.padding(16.dp),
            ) {
                SectionHeader(
                    icon = { Icon(Icons.AutoMirrored.Filled.List, contentDescription = null) },
                    title = stringResource(Res.string.vrm_list_item_label)
                )

                OutlinedButton(
                    enabled = state.isDeviceActive,
                    onClick = { onAction(VrmOutputActions.OnAddVrm) },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Icon(
                        imageVector = Icons.Default.Add,
                        contentDescription = null,
                        modifier = Modifier.size(ButtonDefaults.IconSize)
                    )
                    Spacer(Modifier.width(ButtonDefaults.IconSpacing))
                    Text(stringResource(Res.string.vrm_list_item_add_vrm_button_label))
                }

                AnimatedVisibility(visible = state.vrmConfigurations.isNotEmpty()) {
                    Column {
                        HorizontalDivider(modifier = Modifier.padding(bottom = 4.dp))
                        VrmList(
                            items = state.vrmConfigurations,
                            selectedId = state.selectedVrmId,
                            onClick = { selectedVrm ->
                                if (selectedVrm.id == state.selectedVrmId) {
                                    return@VrmList
                                }
                                onAction(VrmOutputActions.OnUpdateSelectedVrm(selectedVrm.id))
                            },
                            onDelete = {
                                onAction(VrmOutputActions.OnDeleteVrm(it.id))
                            }
                        )
                    }
                }
            }
        }

        // Selected VRM configuration form
        AnimatedVisibility(visible = state.vrmConfigurations.isNotEmpty() && state.selectedVrmId != null) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(20.dp)
            ) {
                // Enable toggle
                OutlinedCard(
                    shape = MaterialTheme.shapes.medium,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    ListItem(
                        leadingContent = {
                            Icon(
                                imageVector = Icons.Default.PowerSettingsNew,
                                contentDescription = null,
                                tint = if (state.vrmEnabled) MaterialTheme.colorScheme.primary
                                else MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        },
                        headlineContent = { Text(stringResource(Res.string.vrm_output_enable_label)) },
                        trailingContent = {
                            Switch(
                                checked = state.vrmEnabled,
                                onCheckedChange = { onAction(VrmOutputActions.OnUpdateVrmEnable(it)) }
                            )
                        }
                    )
                }

                AnimatedVisibility(visible = state.vrmEnabled) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(20.dp)
                    ) {
                        // Endpoint & identification settings
                        OutlinedCard(
                            shape = MaterialTheme.shapes.medium,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(
                                verticalArrangement = Arrangement.spacedBy(16.dp),
                                modifier = Modifier.padding(16.dp),
                            ) {
                                SectionHeader(
                                    icon = { Icon(Icons.Default.Cloud, contentDescription = null) },
                                    title = stringResource(Res.string.vrm_output_connection_details_label)
                                )

                                LargeDropdownMenu(
                                    label = stringResource(Res.string.vrm_output_type_label),
                                    items = VrmOutputType.entries.map { it.toUiText().asString() },
                                    selectedIndex = state.vrmTypeIndex,
                                    onItemSelected = { onAction(VrmOutputActions.OnUpdateVrmType(it)) },
                                )

                                ConfigTextField(
                                    label = stringResource(Res.string.vrm_output_endpoint_label),
                                    value = state.vrmEndpoint,
                                    isError = state.invalidVrmEndpoint,
                                    onValueChange = { onAction(VrmOutputActions.OnUpdateVrmEndpoint(it)) },
                                )

                                // Zatpark specific fields
                                AnimatedVisibility(
                                    visible = state.vrmTypeIndex == VrmOutputType.ZATPARK.ordinal
                                ) {
                                    Column(
                                        modifier = Modifier.fillMaxWidth(),
                                        verticalArrangement = Arrangement.spacedBy(16.dp)
                                    ) {
                                        ConfigTextField(
                                            label = stringResource(Res.string.vrm_output_api_key_label),
                                            value = state.vrmApiKey,
                                            onValueChange = { onAction(VrmOutputActions.OnUpdateVrmApiKey(it)) },
                                        )

                                        ConfigTextField(
                                            label = stringResource(Res.string.vrm_output_site_id_label),
                                            value = state.vrmSiteId,
                                            onValueChange = { onAction(VrmOutputActions.OnUpdateVrmSiteId(it)) },
                                        )
                                    }
                                }

                                // Bay ID selection: link the output to a ROI instance or
                                // assign a fixed identifier that applies to every ANPR message.
                                BayIdSelector(
                                    useRoiIdAsBayId = state.useRoiIdAsBayId,
                                    availableRoi = availableRoi,
                                    roiIdIndex = state.roiIdIndex,
                                    bayIdentifier = state.vrmBayIdentifier,
                                    onToggleMode = { onAction(VrmOutputActions.OnUpdateBayIdMode(it)) },
                                    onRoiSelected = { onAction(VrmOutputActions.OnUpdateRoiBayIdIndex(it)) },
                                    onBayIdentifierChange = {
                                        onAction(VrmOutputActions.OnUpdateVrmBayIdentifier(it))
                                    },
                                )
                            }
                        }

                        // Validation & timing settings
                        OutlinedCard(
                            shape = MaterialTheme.shapes.medium,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(
                                verticalArrangement = Arrangement.spacedBy(16.dp),
                                modifier = Modifier.padding(16.dp),
                            ) {
                                SectionHeader(
                                    icon = { Icon(Icons.AutoMirrored.Filled.Rule, contentDescription = null) },
                                    title = stringResource(Res.string.vrm_output_validation_settings_label)
                                )

                                ConfigTextField(
                                    label = stringResource(Res.string.vrm_output_confidence_threshold_label),
                                    value = state.vrmConfidenceThreshold,
                                    max = maxConfidenceScoreLimit.toString(),
                                    min = miniConfidenceScoreLimit.toString(),
                                    isError = state.invalidConfidenceScoreLimit,
                                    onValueChange = { onAction(VrmOutputActions.OnUpdateVrmConfidenceThreshold(it)) },
                                )

                                ConfigTextField(
                                    label = stringResource(Res.string.vrm_output_time_label),
                                    value = state.vrmTimeoutSeconds,
                                    max = maxTimeoutSecondsLimit.toString(),
                                    min = miniTimeoutSecondsLimit.toString(),
                                    isError = state.invalidTimeoutSeconds,
                                    onValueChange = { onAction(VrmOutputActions.OnUpdateVrmTimeoutSeconds(it)) },
                                )
                            }
                        }

                        // Overlayer
                        OutlinedCard(
                            shape = MaterialTheme.shapes.medium,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(vertical = 8.dp)) {
                                ListItem(
                                    leadingContent = {
                                        Icon(
                                            imageVector = Icons.Default.Layers,
                                            contentDescription = null,
                                            tint = if (state.enableOverlayer) MaterialTheme.colorScheme.primary
                                            else MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    },
                                    headlineContent = { Text(stringResource(Res.string.vrm_overlayer_title)) },
                                    trailingContent = {
                                        Switch(
                                            checked = state.enableOverlayer,
                                            onCheckedChange = { onAction(VrmOutputActions.OnUpdateOverlayerEnable(it)) }
                                        )
                                    }
                                )

                                AnimatedVisibility(visible = state.enableOverlayer) {
                                    Column {
                                        HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))

                                        // Date and time
                                        ListItem(
                                            headlineContent = { Text(stringResource(Res.string.vrm_overlayer_date_and_time_label)) },
                                            trailingContent = {
                                                Switch(
                                                    checked = state.enableOverlayerDateTime,
                                                    onCheckedChange = {
                                                        onAction(VrmOutputActions.OnEnableOverlayerDateTime(it))
                                                    }
                                                )
                                            },
                                            supportingContent = {
                                                AnimatedContent(
                                                    targetState = state.enableOverlayerDateTime,
                                                    label = "overlayerTimeZone"
                                                ) { manualTimeZone ->
                                                    if (manualTimeZone) {
                                                        LargeDropdownMenu(
                                                            items = state.availableTimeZones.map { it.toString() },
                                                            selectedIndex = state.selectedTimeZoneIndex,
                                                            onItemSelected = {
                                                                onAction(VrmOutputActions.OnUpdateTimeZoneIndex(it))
                                                            },
                                                            modifier = Modifier
                                                                .fillMaxWidth()
                                                                .padding(top = 8.dp, end = 16.dp)
                                                        )
                                                    } else {
                                                        Text(stringResource(Res.string.vrm_overlayer_auto_timezone_label))
                                                    }
                                                }
                                            }
                                        )

                                        // Custom label/tag
                                        ListItem(
                                            headlineContent = { Text(stringResource(Res.string.vrm_overlayer_custom_label)) },
                                            trailingContent = {
                                                Switch(
                                                    checked = state.enableOverlayerLabel,
                                                    onCheckedChange = {
                                                        onAction(VrmOutputActions.OnEnableOverlayerLabel(it))
                                                    }
                                                )
                                            },
                                            supportingContent = {
                                                AnimatedVisibility(visible = state.enableOverlayerLabel) {
                                                    ConfigTextField(
                                                        value = state.overlayerLabel,
                                                        onValueChange = {
                                                            onAction(VrmOutputActions.OnUpdateOverlayerLabel(it))
                                                        },
                                                        modifier = Modifier
                                                            .fillMaxWidth()
                                                            .padding(top = 8.dp, end = 16.dp)
                                                    )
                                                }
                                            }
                                        )

                                        // OCR results
                                        ListItem(
                                            headlineContent = { Text(stringResource(Res.string.vrm_overlayer_ocr_results_label)) },
                                            trailingContent = {
                                                Switch(
                                                    checked = state.enableOverlayerOcrResults,
                                                    onCheckedChange = {
                                                        onAction(VrmOutputActions.OnEnableOverlayerOcrResults(it))
                                                    }
                                                )
                                            }
                                        )

                                        // Bay ID
                                        ListItem(
                                            headlineContent = { Text(stringResource(Res.string.vrem_overlayer_bay_id_label)) },
                                            trailingContent = {
                                                Switch(
                                                    checked = state.enableOverlayerBayId,
                                                    onCheckedChange = {
                                                        onAction(VrmOutputActions.OnEnableOverlayerBayId(it))
                                                    }
                                                )
                                            }
                                        )

                                        // Site ID
                                        ListItem(
                                            headlineContent = { Text(stringResource(Res.string.vrm_output_site_id_label)) },
                                            trailingContent = {
                                                Switch(
                                                    checked = state.enableOverlayerSiteId,
                                                    onCheckedChange = {
                                                        onAction(VrmOutputActions.OnEnableOverlayerSiteId(it))
                                                    }
                                                )
                                            }
                                        )

                                        // Position
                                        LargeDropdownMenu(
                                            items = VrmOverlayerPosition.entries.map { it.toUiText().asString() },
                                            selectedIndex = state.overlayerPositionIndex,
                                            onItemSelected = {
                                                onAction(VrmOutputActions.OnUpdateOverlayerPosition(it))
                                            },
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .padding(horizontal = 16.dp)
                                        )
                                        Spacer(modifier = Modifier.height(16.dp))
                                    }
                                }
                            }
                        }

                        // SSL verification
                        OutlinedCard(
                            shape = MaterialTheme.shapes.medium,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            ListItem(
                                leadingContent = {
                                    Icon(
                                        imageVector = if (state.vrmVerifySsl) Icons.Default.Lock else Icons.Default.LockOpen,
                                        contentDescription = null,
                                        tint = if (state.vrmVerifySsl) MaterialTheme.colorScheme.primary
                                        else MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                },
                                headlineContent = { Text(stringResource(Res.string.vrm_output_verify_ssl_label)) },
                                trailingContent = {
                                    Switch(
                                        checked = state.vrmVerifySsl,
                                        onCheckedChange = { onAction(VrmOutputActions.OnUpdateVrmVerifySsl(it)) }
                                    )
                                }
                            )
                        }
                    }
                }
            }
        }

        // Action footer
        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.widthIn(max = 400.dp),
        ) {
            FilledTonalButton(
                enabled = state.isDeviceActive,
                onClick = { onAction(VrmOutputActions.OnLoadCurrentVrmConfigClicked) },
                modifier = Modifier.fillMaxWidth()
            ) {
                Icon(
                    imageVector = Icons.Default.Refresh,
                    contentDescription = null,
                    modifier = Modifier.size(ButtonDefaults.IconSize)
                )
                Spacer(Modifier.width(ButtonDefaults.IconSpacing))
                Text(stringResource(Res.string.load_config_button_title))
            }

            Button(
                enabled = state.isDeviceActive,
                onClick = { onAction(VrmOutputActions.OnSaveClicked) },
                modifier = Modifier.fillMaxWidth()
            ) {
                Icon(
                    imageVector = Icons.Default.Save,
                    contentDescription = null,
                    modifier = Modifier.size(ButtonDefaults.IconSize)
                )
                Spacer(Modifier.width(ButtonDefaults.IconSpacing))
                Text(stringResource(Res.string.save_button_title))
            }
        }

        Spacer(modifier = Modifier.height(32.dp))
    }
}

/**
 * Bay ID selection group. Lets the user either link this VRM output to a dedicated ROI instance or
 * assign a fixed bay identifier that applies to every ANPR message. The input control animates
 * between a ROI picker and a free-text field as the mode toggle changes.
 *
 * @param useRoiIdAsBayId whether the bay ID is derived from a linked ROI instance
 * @param availableRoi selectable ROI identifiers
 * @param roiIdIndex index of the currently selected ROI identifier
 * @param bayIdentifier manually entered bay identifier
 * @param onToggleMode invoked with the new mode when the ROI link toggle changes
 * @param onRoiSelected invoked with the selected ROI index
 * @param onBayIdentifierChange invoked with the manually entered bay identifier
 */
@Composable
private fun BayIdSelector(
    useRoiIdAsBayId: Boolean,
    availableRoi: List<Int>,
    roiIdIndex: Int,
    bayIdentifier: String,
    onToggleMode: (Boolean) -> Unit,
    onRoiSelected: (Int) -> Unit,
    onBayIdentifierChange: (String) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        // ROI link toggle, aligned with the surrounding text fields.
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Icon(
                imageVector = if (useRoiIdAsBayId) Icons.Default.Link else Icons.Default.LinkOff,
                contentDescription = null,
                tint = if (useRoiIdAsBayId) MaterialTheme.colorScheme.primary
                else MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                text = stringResource(Res.string.vrm_output_roi_switch_label),
                style = MaterialTheme.typography.bodyLarge,
                modifier = Modifier.weight(1f)
            )
            Switch(
                checked = useRoiIdAsBayId,
                onCheckedChange = onToggleMode
            )
        }

        InfoNote(text = stringResource(Res.string.vrm_bayid_note))

        // Bay ID value: a ROI instance picker or a manual identifier, crossfading on mode change.
        AnimatedContent(targetState = useRoiIdAsBayId, label = "bayIdMode") { useRoi ->
            if (useRoi) {
                LargeDropdownMenu(
                    label = stringResource(Res.string.vrem_overlayer_bay_id_label),
                    items = availableRoi.map { it.toString() },
                    selectedIndex = roiIdIndex,
                    onItemSelected = onRoiSelected,
                )
            } else {
                ConfigTextField(
                    label = stringResource(Res.string.vrem_overlayer_bay_id_label),
                    value = bayIdentifier,
                    onValueChange = onBayIdentifierChange,
                )
            }
        }
    }
}

/**
 * Tonal informational note: an info icon beside supporting text on a subtle container. Used to
 * explain a setting without competing with the primary controls.
 *
 * @param text note body
 * @param modifier modifier for the note surface
 */
@Composable
private fun InfoNote(
    text: String,
    modifier: Modifier = Modifier,
) {
    Surface(
        color = MaterialTheme.colorScheme.secondaryContainer,
        shape = MaterialTheme.shapes.medium,
        modifier = modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.Top
        ) {
            Icon(
                imageVector = Icons.Default.Info,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSecondaryContainer,
                modifier = Modifier.size(20.dp)
            )
            Text(
                text = text,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSecondaryContainer
            )
        }
    }
}

@Preview
@Composable
fun VrmOutputScreenViewPreview() {
    MaterialTheme {
        Scaffold {
            VrmOutputScreenView(
                state = VrmOutputState(
                    vrmEnabled = true,
                    enableOverlayer = true,
                    enableOverlayerDateTime = false,
                    isDeviceActive = true,
                    selectedVrmId = 0,
                    useRoiIdAsBayId = true,
                    vrmConfigurations = listOf(
                        VrmOutputConfig(id = 0, enabled = true, vrmType = VrmOutputType.ZATPARK, useRoiId = false),
                        VrmOutputConfig(id = 1, enabled = true, vrmType = VrmOutputType.ZATPARK, useRoiId = false),
                    )
                ),
                availableRoi = listOf(1, 2, 3),
                onAction = {},
            )
        }
    }
}
